import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, Check, AlertCircle } from 'lucide-react';
import API from '../../api';
import { useLandingContent } from '../useLandingContent';

export default function Contatti() {
  const { content } = useLandingContent();
  const COMPANY = content.company;
  const [form, setForm] = useState({ nome: '', email: '', telefono: '', servizio: '', messaggio: '' });
  const [status, setStatus] = useState({ state: 'idle', msg: '' });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim() || !form.email.trim() || !form.messaggio.trim()) {
      setStatus({ state: 'error', msg: 'Compila nome, email e messaggio.' });
      return;
    }
    setStatus({ state: 'sending', msg: 'Invio in corso...' });
    try {
      await API.post('/api/public/contact', form);
      setStatus({ state: 'success', msg: 'Richiesta inviata! Ti contatteremo presto.' });
      setForm({ nome: '', email: '', telefono: '', servizio: '', messaggio: '' });
    } catch (err) {
      setStatus({ state: 'error', msg: err.response?.data?.detail || 'Errore invio. Riprova o chiamaci direttamente.' });
    }
  };

  return (
    <section id="contatti" className="py-20 sm:py-28 bg-stone-950 text-white" data-testid="section-contatti">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-14">
          <p className="text-[11px] font-black text-primary uppercase tracking-widest mb-3">— Contatti</p>
          <h2 className="text-3xl sm:text-5xl font-black tracking-tight leading-[1.1]">
            Pronto a partire?<br />
            <span className="text-primary">Parliamone.</span>
          </h2>
          <p className="text-lg text-stone-400 mt-4 max-w-2xl mx-auto">Sopralluogo e preventivo gratuiti. Risposta entro 24 ore lavorative.</p>
        </div>

        <div className="grid lg:grid-cols-5 gap-8">
          {/* Info column */}
          <div className="lg:col-span-2 space-y-4">
            <InfoCard icon={Phone} title="Telefono">
              <a href={`tel:${COMPANY.phoneFisso.replace(/\s/g, '')}`} className="block text-white hover:text-primary font-bold">{COMPANY.phoneFisso}</a>
              <a href={`tel:${COMPANY.phoneGermano.replace(/\s/g, '')}`} className="block text-stone-300 text-sm hover:text-primary">Germano · {COMPANY.phoneGermano}</a>
              <a href={`tel:${COMPANY.phoneSimone.replace(/\s/g, '')}`} className="block text-stone-300 text-sm hover:text-primary">Simone · {COMPANY.phoneSimone}</a>
            </InfoCard>
            <InfoCard icon={Mail} title="Email & PEC">
              <a href={`mailto:${COMPANY.email}`} className="block text-white hover:text-primary font-bold break-all">{COMPANY.email}</a>
              <a href={`mailto:${COMPANY.pec}`} className="block text-stone-300 text-sm hover:text-primary break-all">PEC · {COMPANY.pec}</a>
            </InfoCard>
            <InfoCard icon={MapPin} title="Sede">
              <p className="text-white font-bold">{COMPANY.address.street}</p>
              <p className="text-stone-300 text-sm">{COMPANY.address.cap} {COMPANY.address.city} ({COMPANY.address.province})</p>
            </InfoCard>
            <InfoCard icon={Clock} title="Orari">
              <p className="text-white font-bold">{COMPANY.hours}</p>
              <p className="text-stone-400 text-sm">Sabato e domenica su appuntamento</p>
            </InfoCard>
          </div>

          {/* Form column */}
          <form onSubmit={submit} className="lg:col-span-3 bg-stone-900 rounded-lg p-6 sm:p-8 border border-stone-800" data-testid="contact-form">
            <h3 className="text-2xl font-black mb-1">Richiedi un preventivo</h3>
            <p className="text-stone-400 text-sm mb-6">Tutti i campi con * sono obbligatori.</p>

            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <Input label="Nome e cognome *" value={form.nome} onChange={v => setForm({ ...form, nome: v })} testid="contact-nome" />
              <Input label="Telefono" value={form.telefono} onChange={v => setForm({ ...form, telefono: v })} testid="contact-telefono" type="tel" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <Input label="Email *" value={form.email} onChange={v => setForm({ ...form, email: v })} testid="contact-email" type="email" />
              <div>
                <label className="block text-xs font-bold text-stone-400 uppercase tracking-wider mb-1.5">Servizio di interesse</label>
                <select value={form.servizio} onChange={e => setForm({ ...form, servizio: e.target.value })} data-testid="contact-servizio"
                  className="w-full px-3 py-2.5 bg-stone-800 border border-stone-700 rounded-md text-white focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none">
                  <option value="">Seleziona...</option>
                  <option value="Nuove Costruzioni">Nuove Costruzioni</option>
                  <option value="Ristrutturazioni">Ristrutturazioni</option>
                  <option value="Rifacimento Tetti">Rifacimento Tetti</option>
                  <option value="Realizzazione Piscine">Realizzazione Piscine</option>
                  <option value="Manutenzioni">Manutenzioni</option>
                  <option value="Altro">Altro</option>
                </select>
              </div>
            </div>
            <div className="mb-5">
              <label className="block text-xs font-bold text-stone-400 uppercase tracking-wider mb-1.5">Descrivi il tuo progetto *</label>
              <textarea value={form.messaggio} onChange={e => setForm({ ...form, messaggio: e.target.value })} rows={5} data-testid="contact-messaggio"
                placeholder="Es: ristrutturazione bagno 8mq con sostituzione sanitari, rivestimenti, piatto doccia..."
                className="w-full px-3 py-2.5 bg-stone-800 border border-stone-700 rounded-md text-white focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none resize-none" />
            </div>

            {status.state !== 'idle' && (
              <div data-testid="contact-status" className={`flex items-center gap-2 px-4 py-3 rounded-md mb-4 text-sm font-semibold ${
                status.state === 'success' ? 'bg-green-500/10 border border-green-500/30 text-green-400' :
                status.state === 'error' ? 'bg-red-500/10 border border-red-500/30 text-red-400' :
                'bg-blue-500/10 border border-blue-500/30 text-blue-400'
              }`}>
                {status.state === 'success' ? <Check size={16}/> : status.state === 'error' ? <AlertCircle size={16}/> : <Send size={16} className="animate-pulse"/>}
                {status.msg}
              </div>
            )}

            <button type="submit" disabled={status.state === 'sending'} data-testid="contact-submit"
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-primary text-white font-black rounded-md hover:bg-primary transition-all active:scale-[0.98] disabled:opacity-50 shadow-lg shadow-primary/20">
              {status.state === 'sending' ? 'Invio in corso...' : <>Invia richiesta <Send size={16} /></>}
            </button>
            <p className="text-xs text-stone-500 mt-3 text-center">
              Inviando accetti il trattamento dei dati ai sensi del GDPR. Non verranno condivisi con terzi.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
}

function InfoCard({ icon: Icon, title, children }) {
  return (
    <div className="bg-stone-900 border border-stone-800 rounded-lg p-5">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-8 h-8 rounded-md bg-primary/10 border border-primary/30 flex items-center justify-center">
          <Icon size={14} className="text-primary" />
        </span>
        <p className="text-[10px] font-black text-primary uppercase tracking-widest">{title}</p>
      </div>
      <div className="space-y-0.5">{children}</div>
    </div>
  );
}

function Input({ label, value, onChange, testid, type = 'text' }) {
  return (
    <div>
      <label className="block text-xs font-bold text-stone-400 uppercase tracking-wider mb-1.5">{label}</label>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} data-testid={testid}
        className="w-full px-3 py-2.5 bg-stone-800 border border-stone-700 rounded-md text-white focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none" />
    </div>
  );
}
